'use client';

import { useCallback, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import config from 'config';
import { defaultSettings } from 'constant';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useGetLocationsQuery } from 'services/locationsApi';

import { Box, Stack, useBreakpointValue } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { resetForm, setStep } from 'store/reducers/packagesShopSlice';
import { selectLocation, setRedirectFlow } from 'store/reducers/userSlice';

import { scrollToElement } from 'utils';

import {
  ContentFilter,
  LocationFlowType,
  LocationInterface,
  PackagePurchaseStep
} from 'types';

import { LocationStep } from 'components/LocationStep';
import { Loader } from 'ui';
import { PackagesListStep } from './components';

const {
  shop: {
    packages: { banner, bannerMobile }
  }
} = config;

interface ShopPackagesFlowProps {
  promoId: number;
  paramsLocationId?: null | string;
}

const ShopPackagesFlow = ({
  promoId,
  paramsLocationId
}: ShopPackagesFlowProps) => {
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const { user, selectedLocation } = useAppSelector((state) => state.user);
  const { data: locations = [], isLoading } = useGetLocationsQuery(
    ContentFilter.ServicesOnly
  );
  const { step } = useAppSelector((state) => state.packagesShop);
  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { fallback: 'md' }
  );
  const containerElement = useRef(null);

  const homeLocationId =
    (paramsLocationId && Number(paramsLocationId)) ??
    selectedLocation?.id ??
    user?.home_store_id;

  const homeLocations = locations?.filter(
    (storeLocation) => homeLocationId === storeLocation.id
  );
  const firstItem = homeLocations[0];

  const scrollIntoView = () => {
    scrollToElement(containerElement, 'instant');
  };

  const selectPackageFlow = useCallback(
    () => dispatch(setRedirectFlow(LocationFlowType.PACKAGES)),
    [dispatch]
  );

  const handleReset = () => {
    dispatch(resetForm());
  };

  const onChangeStep = (value: PackagePurchaseStep) => {
    if (value === PackagePurchaseStep.LOCATION) {
      handleReset();
    } else {
      dispatch(setStep(value));
    }
    scrollIntoView();
  };

  const onPackagesList = () => onChangeStep(PackagePurchaseStep.PACKAGES_LIST);

  const handleSelectLocation = (value: LocationInterface) => {
    dispatch(selectLocation(value));
    onPackagesList();
  };

  useEffect(() => {
    selectPackageFlow();
  }, [selectPackageFlow]);

  useEffect(() => {
    if (homeLocationId && firstItem) {
      handleSelectLocation(firstItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, Boolean(locations.length), firstItem]);

  const skipLocationStep = () => {
    onPackagesList();
    handleSelectLocation(firstItem);
  };

  const renderStep = () => {
    switch (step) {
      case PackagePurchaseStep.PACKAGES_LIST:
        return <PackagesListStep onBack={handleReset} promoId={promoId} />;

      default:
        return (
          <LocationStep
            onSkip={skipLocationStep}
            onSelectLocation={handleSelectLocation}
            locationFlow={LocationFlowType.PACKAGES}
            scrollIntoView={scrollIntoView}
          />
        );
    }
  };

  const banners = isMobile ? bannerMobile : banner;

  return (
    <Stack
      ref={containerElement}
      width='100%'
      alignItems='stretch'
      gap={{ base: '20px', md: '40px' }}
      px={{ base: 0, md: '25px' }}
      pt={{ base: 0, md: '15px', lg: '30px' }}
    >
      <Slider
        {...defaultSettings}
        fade
        speed={2000}
        adaptiveHeight
        variableWidth={false}
      >
        {banners.map((img: string) => (
          <Box key={img}>
            <Image
              src={img}
              alt='Special proposition banner'
              width={1120}
              height={378}
              priority
              style={{
                width: '100%',
                height: '100%'
              }}
            />
          </Box>
        ))}
      </Slider>

      {isLoading ? (
        <Loader absolute />
      ) : (
        <Box w='full' padding={{ base: '0 15px', md: 0 }} mt='20px'>
          {renderStep()}
        </Box>
      )}
    </Stack>
  );
};

export default ShopPackagesFlow;
